import { FORBIDDEN_KEYS } from './forbiddenKeys';
import type { PathSegment } from './types';

/**
 * Resolve a pre-parsed dot/wildcard path against a runtime context.
 *
 * `getByPath` is intentionally total — it never throws. Missing keys, null or
 * undefined intermediates, primitive intermediates, and forbidden prototype
 * keys ({@link FORBIDDEN_KEYS}) all resolve to `undefined` per ADR-12
 * ("missing paths resolve to `undefined`"). Parser-level problems are
 * surfaced by `parse()` via {@link InterpolationError}; runtime lookups stay
 * silent.
 *
 * The `[*]` wildcard flattens **exactly one** level of an array per `[*]`,
 * so `a.b[*].c` applied to `{ a: { b: [{ c: 1 }, { c: 2 }] } }` returns
 * `[1, 2]`. When the post-wildcard lookup itself resolves to an array,
 * that array is preserved (it is **not** absorbed into the outer
 * wildcard): `a[*].b` against `{ a: [{ b: [1, 2] }, { b: [3, 4] }] }`
 * returns `[[1, 2], [3, 4]]`, not `[1, 2, 3, 4]`. This keeps structure
 * recoverable for DB-1.3's JSON-body serialization. Multi-level fan-out
 * requires an explicit second wildcard (`a[*].b[*]`), which then yields
 * one level of flattening per `[*]` — i.e. `[[1, 2], [3, 4]]`. When a
 * single flat list is desired (e.g. for comma-joined query-param
 * serialization), callers flatten downstream.
 *
 * Empty segment arrays return the context as-is (useful when callers want a
 * "root" reference — e.g. a single `{{context}}` expression, though this is
 * not a first-class use case in ADR-12).
 *
 * @param context - Arbitrary runtime context. Typically an object keyed by
 *   scope (`context`, `results`, `item`, …).
 * @param segments - Pre-parsed path produced by `parse()`.
 *
 * @example
 * getByPath({ context: { tenantId: 'acme' } }, [
 *   { type: 'key', name: 'context' },
 *   { type: 'key', name: 'tenantId' },
 * ]);
 * // => 'acme'
 *
 * @example
 * getByPath(
 *   { results: { programs: [{ id: 'p1' }, { id: 'p2' }] } },
 *   [
 *     { type: 'key', name: 'results' },
 *     { type: 'key', name: 'programs' },
 *     { type: 'wildcard' },
 *     { type: 'key', name: 'id' },
 *   ],
 * );
 * // => ['p1', 'p2']
 */
export function getByPath(
  context: unknown,
  segments: ReadonlyArray<PathSegment>,
): unknown {
  return walk(context, segments, 0);
}

function walk(
  value: unknown,
  segments: ReadonlyArray<PathSegment>,
  index: number,
): unknown {
  if (index >= segments.length) {
    return value;
  }

  const seg = segments[index];

  if (seg.type === 'wildcard') {
    if (!Array.isArray(value)) {
      return undefined;
    }
    // Strict one-level flatten: each element's sub-walk is pushed as-is,
    // preserving any array returned by the remaining segments. This keeps
    // `a[*].b` where `b: number[]` structurally honest
    // (`[[…], […]]` rather than `[…flattened…]`), so DB-1.3 can serialize
    // to JSON bodies without losing shape. Multi-level fan-out requires
    // an explicit second wildcard (`a[*].b[*]`), which then yields
    // `[[…], […]]` as well — one level per `[*]`.
    const out: unknown[] = [];
    for (let i = 0; i < value.length; i++) {
      out.push(walk(value[i], segments, index + 1));
    }
    return out;
  }

  if (value === null || value === undefined) {
    return undefined;
  }
  if (typeof value !== 'object') {
    return undefined;
  }
  if (FORBIDDEN_KEYS.has(seg.name)) {
    return undefined;
  }
  if (!Object.prototype.hasOwnProperty.call(value, seg.name)) {
    return undefined;
  }

  const next = (value as Record<string, unknown>)[seg.name];
  return walk(next, segments, index + 1);
}
